<template>
  <div class="station-level-analytics">
    <a-row :gutter="[16, 16]">
      <!-- Row 1: Total Cycles Chart | Cycle Time Analysis Chart -->
      <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
        <a-card
          title="Total Number of Cycles"
          size="small"
          class="chart-card"
          :loading="loading"
        >
          <highcharts :options="getTotalCyclesChartOptions()" />
        </a-card>
      </a-col>

      <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
        <CombinedCycleAnalysisChart
          :takt-data="taktData"
          :std-deviation-data="analyticsData"
          :loading="loading"
          :time-unit="timeUnit"
        />
      </a-col>

      <!-- Row 2: Total Work Time Chart | Median Wait Time Chart -->
      <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
        <a-card
          :title="totalWorkTimeTitle"
          size="small"
          class="chart-card"
          :loading="loading"
        >
          <highcharts :options="getTotalWorkTimeChartOptions()" />
        </a-card>
      </a-col>

      <a-col :xs="24" :sm="24" :md="12" :lg="12" :xl="12">
        <a-card
          :title="medianWaitTimeTitle"
          size="small"
          class="chart-card"
          :loading="loading"
        >
          <highcharts :options="getMedianWaitTimeChartOptions()" />
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue';
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import {
  convertTime,
  convertTimeArray,
  getUnitLabel,
} from 'src/utils/timeConversion';
import {
  calculateRelativeDeviation,
  calculateInverseDeviation,
  getDeviationColor,
  getInverseDeviationColor,
} from 'src/utils/waitTimeCalculations';
import CombinedCycleAnalysisChart from './CombinedCycleAnalysisChart.vue';

// Initialize highcharts-more for error bars
if (typeof HighchartsMore === 'function') {
  HighchartsMore(Highcharts);
}

export default {
  name: 'StationLevelAnalytics',
  components: {
    highcharts: Chart,
    CombinedCycleAnalysisChart,
  },
  props: {
    stationName: {
      type: String,
      default: 'Station 1',
    },
    analyticsData: {
      type: Object,
      default: () => ({}),
    },
    taktData: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    timeUnit: {
      type: String,
      default: 'seconds',
      validator: (value) => ['seconds', 'minutes'].includes(value),
    },
  },
  data() {
    return {
      // Sample data structure matching API response
      sampleData: {
        labels: ['Engine Assembly', 'Quality Inspection', 'Final Testing'],
        total_cycles: [150, 145, 148],
        avg_cycle_time: [245.5, 180.25, 320.75],
        total_work_time: [36825.0, 26136.25, 47471.0],
        wait_time_by_previous_task: [25.3, 20.45, 22.9],
      },
    };
  },
  computed: {
    chartData() {
      return Object.keys(this.analyticsData).length > 0
        ? this.analyticsData
        : {
            labels: [],
            total_cycles: [],
            avg_cycle_time: [],
            total_work_time: [],
            wait_time_by_previous_task: [],
          };
    },
    unitLabel() {
      return getUnitLabel(this.timeUnit);
    },
    unitLabelAbbr() {
      return getUnitLabel(this.timeUnit, true);
    },
    avgCycleTimeTitle() {
      return `Average Cycle Time (${this.unitLabel})`;
    },
    totalWorkTimeTitle() {
      return `Total Work Time (${this.unitLabel})`;
    },
    medianWaitTimeTitle() {
      return `Average Post Cycle Start Delay By Task`;
    },
  },
  methods: {
    getTotalCyclesChartOptions() {
      return {
        chart: {
          type: 'column',
          height: 350,
          backgroundColor: 'transparent',
          zoomType: 'xy',
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: this.chartData.labels,
          labels: {
            style: {
              fontSize: '11px',
            },
          },
        },
        yAxis: {
          title: {
            text: 'Number of Cycles',
          },
          labels: {
            format: '{value}',
          },
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          column: {
            colorByPoint: true,
            dataLabels: {
              enabled: true,
              format: '{y}',
              style: {
                fontSize: '10px',
              },
            },
          },
        },
        colors: [
          '#5470c6',
          '#91cc75',
          '#fac858',
          '#ee6666',
          '#73c0de',
          '#3ba272',
          '#fc8452',
          '#9a60b4',
        ],
        series: [
          {
            name: 'Total Cycles',
            data: this.chartData.total_cycles,
          },
        ],
        credits: {
          enabled: false,
        },
        tooltip: {
          pointFormat: '<b>{point.y}</b> cycles',
        },
      };
    },

    getAvgCycleTimeChartOptions() {
      const convertedData = convertTimeArray(this.chartData.avg_cycle_time, this.timeUnit);
      return {
        chart: {
          type: 'column',
          height: 300,
          backgroundColor: 'transparent',
          zoomType: 'xy',
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: this.chartData.labels,
          labels: {
            style: {
              fontSize: '11px',
            },
          },
        },
        yAxis: {
          title: {
            text: `Time (${this.unitLabel})`,
          },
          labels: {
            format: `{value}${this.unitLabelAbbr}`,
          },
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          column: {
            colorByPoint: true,
            dataLabels: {
              enabled: true,
              format: `{y:.1f}${this.unitLabelAbbr}`,
              style: {
                fontSize: '10px',
              },
            },
          },
        },
        colors: [
          '#5470c6',
          '#91cc75',
          '#fac858',
          '#ee6666',
          '#73c0de',
          '#3ba272',
          '#fc8452',
          '#9a60b4',
        ],
        series: [
          {
            name: 'Average Cycle Time',
            data: convertedData,
          },
        ],
        credits: {
          enabled: false,
        },
        tooltip: {
          pointFormat: `<b>{point.y:.2f}</b> ${this.unitLabel}`,
        },
      };
    },

    getTotalWorkTimeChartOptions() {
      const convertedData = convertTimeArray(this.chartData.total_work_time, this.timeUnit, 1);
      return {
        chart: {
          type: 'column',
          height: 350,
          backgroundColor: 'transparent',
          zoomType: 'xy',
        },
        customData: {
          unitLabel: this.unitLabel,
          unitLabelAbbr: this.unitLabelAbbr,
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: this.chartData.labels,
          labels: {
            style: {
              fontSize: '11px',
            },
          },
        },
        yAxis: {
          title: {
            text: `Time (${this.unitLabel})`,
          },
          labels: {
            format: `{value}${this.unitLabelAbbr}`,
          },
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          column: {
            colorByPoint: true,
            dataLabels: {
              enabled: true,
              format: `{y:.1f}${this.unitLabelAbbr}`,
              style: {
                fontSize: '10px',
              },
            },
          },
        },
        colors: [
          '#5470c6',
          '#91cc75',
          '#fac858',
          '#ee6666',
          '#73c0de',
          '#3ba272',
          '#fc8452',
          '#9a60b4',
        ],
        series: [
          {
            name: 'Total Work Time',
            data: convertedData,
          },
        ],
        credits: {
          enabled: false,
        },
        tooltip: {
          useHTML: true,
          backgroundColor: '#ffffff',
          borderColor: '#cccccc',
          borderRadius: 8,
          borderWidth: 1,
          shadow: true,
          formatter: function () {
            const unitLabelAbbr =
              this.series.chart.options.customData?.unitLabelAbbr || 's';
            let tooltip = `<div style="padding: 4px;">`;
            tooltip += `<div style="padding-bottom: 6px; font-weight: bold; font-size: 13px;">${this.x}</div>`;
            tooltip += '<div style="border-top: 1px solid #e0e0e0; margin: 4px 0;"></div>';
            tooltip += `<div style="margin: 6px 0;">`;
            tooltip += `<span style="color:${this.point.color}">\u25CF</span> <b>Total Work Time:</b> ${this.y.toFixed(1)}${unitLabelAbbr}`;
            tooltip += `</div>`;
            tooltip += `</div>`;
            return tooltip;
          },
        },
      };
    },

    getMedianWaitTimeChartOptions() {
      const rawData = this.chartData.wait_time_by_previous_task || [];
      const stdDevData = this.chartData.std_deviation_wait_time_by_previous_task || [];
      const labels = this.chartData.labels || [];
      const { percentages, maximum, originalValues } = calculateInverseDeviation(rawData);
      const unitLabel = this.unitLabel;
      const unitLabelAbbr = this.unitLabelAbbr;
      const colors = [
        '#5470c6',
        '#91cc75',
        '#fac858',
        '#ee6666',
        '#73c0de',
        '#3ba272',
        '#fc8452',
        '#9a60b4',
      ];

      // Filter out baseline (0% - highest value) and bars with 100% or 0 value
      const filteredData = [];
      const filteredLabels = [];
      const filteredStdDevs = [];
      const filteredOriginalValues = [];

      percentages.forEach((pct, i) => {
        // Skip if percentage is 0 (baseline/maximum) or 100 (zero value)
        if (pct === 0 || pct === 100 || originalValues[i] === 0) {
          return;
        }
        filteredData.push({
          y: pct,
          originalValue: convertTime(originalValues[i], this.timeUnit),
          color: colors[filteredLabels.length % colors.length],
        });
        filteredLabels.push(labels[i]);
        filteredStdDevs.push(stdDevData[i] || 0);
        filteredOriginalValues.push(originalValues[i]);
      });

      // Calculate error bar data based on wait time and std deviation
      const errorBarData = filteredOriginalValues.map((waitTime, index) => {
        const stdDev = filteredStdDevs[index] || 0;
        const convertedWaitTime = convertTime(waitTime, this.timeUnit);
        const convertedStdDev = convertTime(stdDev, this.timeUnit);
        const lowerBound = Math.max(0, convertedWaitTime - convertedStdDev);
        const upperBound = convertedWaitTime + convertedStdDev;
        return [lowerBound, upperBound];
      });

      // Convert filtered original values for the chart
      const convertedFilteredValues = filteredOriginalValues.map((v) =>
        convertTime(v, this.timeUnit)
      );

      // Build series data with actual wait time values (not percentages)
      const seriesData = convertedFilteredValues.map((val, i) => ({
        y: val,
        originalPercentage: filteredData[i].y,
        stdDev: convertTime(filteredStdDevs[i], this.timeUnit),
        color: colors[i % colors.length],
      }));

      return {
        chart: {
          type: 'column',
          height: 350,
          backgroundColor: 'transparent',
          zoomType: 'xy',
        },
        customData: {
          unitLabel: unitLabel,
          unitLabelAbbr: unitLabelAbbr,
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: filteredLabels,
          labels: {
            style: {
              fontSize: '11px',
            },
          },
        },
        yAxis: {
          title: {
            text: `Avg Post Cycle Start Delay (${unitLabel})`,
          },
          labels: {
            format: `{value}${unitLabelAbbr}`,
          },
          min: 0,
        },
        legend: {
          align: 'center',
          verticalAlign: 'bottom',
          layout: 'horizontal',
        },
        plotOptions: {
          column: {
            colorByPoint: true,
            borderRadius: 3,
            dataLabels: {
              enabled: false,
            },
          },
          errorbar: {
            color: '#d48265',
            lineWidth: 2,
            whiskerLength: '50%',
            whiskerWidth: 2,
            stemWidth: 2,
          },
        },
        colors: colors,
        series: [
          {
            name: 'Avg Post Cycle Start Delay',
            type: 'column',
            data: seriesData,
            showInLegend: false,
            zIndex: 1,
          },
          {
            name: 'Variability (±Std Dev)',
            type: 'errorbar',
            data: errorBarData,
            linkedTo: ':previous',
            color: '#d48265',
            whiskerLength: '50%',
            zIndex: 3,
          },
        ],
        credits: {
          enabled: false,
        },
        tooltip: {
          shared: true,
          crosshairs: true,
          useHTML: true,
          backgroundColor: '#ffffff',
          borderColor: '#cccccc',
          borderRadius: 8,
          borderWidth: 1,
          shadow: true,
          style: {
            opacity: 1,
          },
          formatter: function () {
            const self = this;
            const customData = self.series.chart.options.customData;
            const unitLabelAbbr = customData.unitLabelAbbr;

            // Find the column point (not errorbar)
            const columnPoint = self.points.find(
              (p) => p.series.type === 'column'
            );
            const errorBarPoint = self.points.find(
              (p) => p.series.type === 'errorbar'
            );

            let tooltip = `<div style="padding: 4px; background: #fff;">`;
            tooltip += `<div style="padding-bottom: 6px; font-weight: bold; font-size: 13px;">${self.x}</div>`;
            tooltip +=
              '<div style="border-top: 1px solid #e0e0e0; margin: 4px 0;"></div>';

            if (columnPoint) {
              tooltip += `<div style="margin: 6px 0;">`;
              tooltip += `<span style="color:${columnPoint.color}">\u25CF</span> <b>Avg Delay:</b> ${columnPoint.y.toFixed(2)}${unitLabelAbbr}`;
              tooltip += `</div>`;

              if (columnPoint.point.stdDev !== undefined) {
                tooltip += `<div style="margin: 6px 0;">`;
                tooltip += `<span style="color:#d48265">\u25CF</span> <b>Std Deviation:</b> ±${columnPoint.point.stdDev.toFixed(2)}${unitLabelAbbr}`;
                tooltip += `</div>`;
              }

              if (errorBarPoint) {
                tooltip += `<div style="margin: 6px 0;">`;
                tooltip += `<span style="color:#666">\u25CF</span> <b>Range:</b> ${errorBarPoint.point.low.toFixed(2)}${unitLabelAbbr} – ${errorBarPoint.point.high.toFixed(2)}${unitLabelAbbr}`;
                tooltip += `</div>`;
              }

              if (columnPoint.point.originalPercentage !== undefined) {
                tooltip +=
                  '<div style="border-top: 1px solid #e0e0e0; margin: 6px 0;"></div>';
                tooltip += `<div style="margin: 6px 0;">`;
                tooltip += `<span style="color:#91cc75">\u25CF</span> <b>% Reduction from Max:</b> ${columnPoint.point.originalPercentage.toFixed(1)}%`;
                tooltip += `</div>`;
              }
            }

            tooltip += `</div>`;
            return tooltip;
          },
        },
      };
    },
  },
};
</script>

<style scoped>
.station-level-analytics {
  margin-bottom: 24px;
}

.chart-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  transition: box-shadow 0.3s ease;
}

.chart-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.station-level-analytics :deep(.ant-card) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
}
</style>
